import React, { useEffect, useState } from 'react';
import { Box } from 'native-base';
import { useOffersScreen, useSpringBigStore } from '@springbig/shared';
import RedeemedRewardModal from '~/shared/RedeemedReward';
import { useAuthorization } from '~/hooks/auth';
import OffersCard, { OffersCardSkeleton } from './components/OfferCard';
import ExtensionDetailsModal from '~/shared/ExtensionDetailsModal';
import ClaimGiftCardModal from '~/shared/ClaimGiftCardModal';
import { useAppNavigate } from '~/pages';
import SuspendedMerchantScreen from '~/shared/SuspendedMerchantScreen';
import { NoDataPlaceholder } from '~/shared/NoDataPlaceholder';
import { faGift } from '@fortawesome/free-solid-svg-icons';
import ConditionalForm, { AutoConnectFieldType } from './components/MemberForm';
import { Button, Section } from '~/shared/Theme';

const OffersScreen = () => {

    const {
        rewardGrants,
        successRedeem,
        rewardActiveId,
        loadingActivation,
        onRefresh,
        handleAcceptRedeemReward,
        handleCancelRedeem,
        handleAcceptRedeem,
        handleBonusRewards,
        member,
        showAlert,
        setShowAlert,
        isLoading,
        isRedeeming,
        setSuccessRedeem,
        desiredActions,
        redeemOfferInStore
    } = useOffersScreen();

    const { navigate, params } = useAppNavigate();
    const { id } = params || {};

    const { walletId } = useSpringBigStore();
    const [showConditionalForm, setShowConditionalForm] =
        useState<boolean>(false);

    const [selectedTypeId, setSelectedTypeId] =
        useState<AutoConnectFieldType>();

    const [showGiftCardModal, setShowGiftCardModal] =
        useState(false);
    const [showExtensionDetailsModal, setShowExtensionDetailsModal] =
        useState(false);

    useEffect(() => {

        if (id) {
            const int = Number(id);

            if (!isNaN(int)) {
                setTimeout(() => {

                    const grant =
                        rewardGrants.find((rg) => rg.id === int) || rewardGrants.find((rg) => rg.reward.id === int);

                    setShowAlert({ status: true, id: grant.id, title: null });
                    setShowExtensionDetailsModal(true);
                }, 1000);
            }
        }
    }, [id]);

    useEffect(() => {

        if (showAlert) {
            const grant =
                rewardGrants.find((rg) => rg.id === showAlert.id) || rewardGrants.find((rg) => rg.reward.id === showAlert.id);

            setShowGiftCardModal(grant?.reward?.reward_type === 'GiftCardReward');
            setShowExtensionDetailsModal(grant?.reward?.reward_type !== 'GiftCardReward');
        }
        else {
            setShowGiftCardModal(false);
            setShowExtensionDetailsModal(false);
        }
    }, [showAlert]);

    const { isLoadingAuth } = useAuthorization();

    const handleModalClose = (): void => {

        handleCancelRedeem();
    };

    const onGetOffer = (typeId : number) => {

        switch (typeId) {
        case 8:
            setSelectedTypeId(8);
            setShowConditionalForm(true);
            return;
        case 10:
            setSelectedTypeId(10);
            setShowConditionalForm(true);
            return;
        case 9:
            navigate(`w/${walletId}/profile/interests`, { state: { isInterestScreen: true } });
            return;
        }
    };

    const header = (
        member &&
        member.referrals.active ?
            <Button
                text={'offers.refer_friend'}
                onPress={() => navigate(`w/${params.wallet_id}/refer`)}
            />
            :
            null
    );

    if ((!rewardGrants || !rewardGrants.length) && (!desiredActions || !(desiredActions && desiredActions.length)) && !isLoading) {
        return (
            <Section
                pageHeaderChildren={header}
            >
                <NoDataPlaceholder
                    title={'common.offers_placeholder_title'}
                    message={'common.offers_placeholder_subtitle'}
                    callback={onRefresh}
                    isLoading={isLoading}
                    iconName={faGift}
                />
            </Section>
        );
    }

    return (
        <Section
            pageHeaderChildren={header}
        >
            <Box pt={4}>
                {(isLoading || isLoadingAuth) ?
                    <OffersCardSkeleton />
                    :
                    desiredActions &&
                        <OffersCard
                            desiredActions={desiredActions as any}
                            onGetOffer={onGetOffer}
                            isLoading={isLoading || isLoadingAuth}
                        />
                }
                <OffersCard
                    rewardGrants={rewardGrants}
                    handleAcceptRedeemReward={handleAcceptRedeemReward}
                    handleBonusRewards={handleBonusRewards}
                    rewardActiveId={rewardActiveId}
                    loadingActivation={loadingActivation}
                    isLoading={isLoading || isLoadingAuth}
                />
            </Box>

            { member && member.show_pending_modal &&
                <SuspendedMerchantScreen />
            }

            { showGiftCardModal &&
                <ClaimGiftCardModal
                    rewardGrantId={showAlert.id}
                    closeModal={handleCancelRedeem}
                />
            }
            { (showExtensionDetailsModal) &&
                <ExtensionDetailsModal
                    showModal={showAlert.status}
                    extensionId={showAlert.id}
                    type={'offers'}
                    onCloseModal={handleModalClose}
                    onRedeem={handleAcceptRedeem}
                    isInStore={redeemOfferInStore}
                    isRedeeming={isRedeeming}
                />
            }
            { successRedeem &&
                <RedeemedRewardModal
                    memberPhoneNumber={member?.phone_number || ''}
                    createdAt={successRedeem.created_at}
                    confirmationNumber={successRedeem.external_discount_code || successRedeem.id}
                    isExternalSource={Boolean(successRedeem.external_discount_code)}
                    isInStore={redeemOfferInStore}
                    returnWallet={() => setSuccessRedeem(null)}
                    isOpen={true}
                    image={successRedeem.reward.images}
                    expires={successRedeem.expires}
                    description={successRedeem.reward.description || ''}
                    title={successRedeem.reward.item_name}
                />
            }
            <ConditionalForm
                isOpen={showConditionalForm}
                closeModal={() => setShowConditionalForm(false)}
                fieldType={selectedTypeId}
            />
        </Section>
    );
};

export default OffersScreen;
